/* eslint-disable no-await-in-loop */
/* eslint-disable complexity */
import PDFDocument from "pdfkit";
import {
  addImgData,
  addSVG,
  boxWithText,
  camelCaseToTitleCase,
  drawPageTemplate,
  drawSolidCircle,
  findDrawAtCoords,
  getDocCurrPosition,
  getEdgeSpacing,
  getNextYTextPosition,
  printRowOfColumizedText,
  printText,
  putDotOnChart,
  putTwoDotsOnChartWithLine,
  addPageNumber
} from "./util";
import { EncounterPrintable } from "./types/obschart.interfaces";
import { testData } from "./test-data";
import {
  cellHorizDistance,
  cellHorizDistanceNeuro,
  cellVertDistance,
  cellVertDistanceNeuro,
  countCellsAcross,
  countCellsHeigh,
  dotRadius,
  heightLimbNeuroRow,
  initalsNtlhsCell,
  neuroLeftArmStartX,
  neuroLeftArmStartY,
  printableNeuroObsChartDef,
  getPrintableObsChartDef,
  startOfPageY,
  tlhsCell,
  tlhsCellHeader
} from "./config/chartdef";
import {
  momentHospitalTime,
  getInitialsObsChart,
  cleanCreatedNameObsChart,
} from "@/global";
import { encounter2PrintableEncounter } from "./marshalling";
import { mockEncounter01 } from "./mocks/mockEncounter01";
// import { TestRecord_86b0hegk5 } from "./mocks/86b0hegk5";
// import { TestRecord_86b0hejq8 } from "./mocks/86b0hejq8";
import BlobStream from "blob-stream";
import { obsMetricNames, obsMetricToLabel } from "../../resources/observations_metricUtils";
import { get } from "lodash";
import { O2_SATURATION_LIMITS } from "@/components/ObservationChart";
import type { Observation } from "@/resources/types";
import type { ObsInput, ObsInputOriginValue } from "@/components/ObservationChart";
import { type PatientEncounter } from "@/resources/types";
import { getLastRecordedObservationValues } from "@/util/util-obs";

const moment = momentHospitalTime;

const USE_MOCK_ENCOUNTER_FOR_PDF_PREVIEW = false;

const getNewsColorBg = (news: number | undefined): string => {
  switch (news) {
    case 1:
      return "#f9face"; // Yellow
    case 2:
      return "#f5e8cf"; // Orange
    case 3:
      return "#ebc7be"; // Red
    case 4:
      return "#ccc0de"; // purple
    default:
      return "#ffffff"; // white
  }
};

type SummaryMetadataField = {
  label: string;
  value: string;
};

const toPrintableText = (value: unknown, fallback = "Not Supplied") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text.length > 0 ? text : fallback;
};

const printSummaryMetadataFlow = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable,
  isOdd_: boolean,
  startY: number,
  fields: SummaryMetadataField[],
  opts?: {
    fontSize?: number;
    fontFamily?: "arial" | "arial-bold" | "arial-italic" | "arial-bold-italic";
    columnGap?: number;
    rowGap?: number;
    width?: number;
  },
) => {
  let isOdd = isOdd_;
  const fontSize = opts?.fontSize ?? 10;
  const fontFamily = opts?.fontFamily ?? "arial";
  const columnGap = opts?.columnGap ?? 16;
  const rowGap = opts?.rowGap ?? 4;
  const lineWidth = opts?.width ?? 490;

  const lineHeight = (() => {
    doc.font(fontFamily).fontSize(fontSize);
    return doc.heightOfString("Ag", { width: lineWidth });
  })();

  let currentY = startY;
  let currentX = getEdgeSpacing(isOdd).leftHandEdgeTxt;

  for (const field of fields) {
    const text = `${field.label}: ${toPrintableText(field.value, "-")}`;
    doc.font(fontFamily).fontSize(fontSize);
    const fieldWidth = doc.widthOfString(text);
    const leftEdge = getEdgeSpacing(isOdd).leftHandEdgeTxt;
    const rightEdge = leftEdge + lineWidth;

    if (fieldWidth > lineWidth) {
      if (currentX !== leftEdge) {
        currentY += lineHeight + rowGap;
        currentX = leftEdge;
      }

      isOdd = await printText(
        doc,
        printData,
        text,
        [currentX, currentY],
        isOdd,
        {
          fontFamily,
          fontSize,
          width: lineWidth,
          ignoreOverflow: true,
        },
      );
      currentY = doc.y + rowGap;
      currentX = leftEdge;
      continue;
    }

    if (currentX !== leftEdge && (currentX + fieldWidth) > rightEdge) {
      currentY += lineHeight + rowGap;
      currentX = leftEdge;
    }

    isOdd = await printText(
      doc,
      printData,
      text,
      [currentX, currentY],
      isOdd,
      {
        fontFamily,
        fontSize,
        width: fieldWidth + 2,
        ignoreOverflow: true,
      },
    );
    currentX += fieldWidth + columnGap;
  }

  return {
    isOdd,
    nextY: currentY + lineHeight,
  };
};

/**
 * Trier Observations Chart
 */

// #region Generate PDF Document - Page 1

const addPage1 = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable
) => {

  let isOdd = true;
  await drawPageTemplate(doc, printData, "odd");

  const txtBodyCommonOpts = {
    fontSize: 10,
    fontFamily: "arial",
    width: 490,
  } as const;
  const txtTitleCommonOpts = {
    ...txtBodyCommonOpts,
    fontFamily: "arial-bold",
  } as const;

  let cursorY = startOfPageY + 2;
  const leftEdge = () => getEdgeSpacing(isOdd).leftHandEdgeTxt;

  const topRow = await printSummaryMetadataFlow(
    doc,
    printData,
    isOdd,
    cursorY,
    [
      { label: "ATS", value: `${printData.encounter.ats || "-"}` },
      { label: "Encounter Start", value: printData.encounter.encounterStart },
      { label: "Age", value: printData.encounter.ageSexDisplay },
    ],
    {
      fontFamily: "arial-bold",
      fontSize: 14,
      columnGap: 22,
      rowGap: 6,
    },
  );
  isOdd = topRow.isOdd;
  cursorY = topRow.nextY + 6;

  const mergedMetaRow = await printSummaryMetadataFlow(
    doc,
    printData,
    isOdd,
    cursorY,
    [
      { label: "Triage Nurse", value: printData.encounter.triageNurseName },
      { label: "Location", value: printData.encounter.locationDisplay },
      { label: "Arrival Mode", value: printData.encounter.arrivalModeDisplay },
      { label: "Complaint Code", value: printData.encounter.complaintCodeFullPath },
    ],
    {
      fontFamily: "arial-bold",
      fontSize: 10,
      columnGap: 14,
      rowGap: 4,
    },
  );
  isOdd = mergedMetaRow.isOdd;
  cursorY = mergedMetaRow.nextY + 8;

  /**
   * Presenting problem + photos block
   */
  const photoTopY = cursorY;

  let patientImgData = null;
  let problemImgData = null;
  if (printData.encounter.photos && Array.isArray(printData.encounter.photos)) {
    for (const photo of printData.encounter.photos) {
      if (photo.type === "patient") {
        patientImgData = photo.data;
      } else if (photo.type === "problem") {
        problemImgData = photo.data;
      }
    }
  }
  if (patientImgData) {
    await addImgData(
      doc,
      patientImgData,
      [300, photoTopY],
      [110.9, 139],
      true,
    );
  } else {
    await addSVG(
      doc,
      "photo-placeholder-patient.svg",
      [300, photoTopY],
      undefined,
      { preserveAspectRatio: "xMinYMin meet", height: 140 }
    );
  }
  if (problemImgData) {
    await addImgData(
      doc,
      problemImgData,
      [430, photoTopY],
      [110.9, 139],
      true,
    );
  } else {
    await addSVG(
      doc,
      "photo-placeholder-problem.svg",
      [430, photoTopY],
      undefined,
      { preserveAspectRatio: "xMinYMin meet", height: 140 }
    );
  }

  const leftColWidth = 220;
  isOdd = await printText(
    doc,
    printData,
    "Presenting Problem (Triage Nurse):",
    [leftEdge(), photoTopY],
    isOdd,
    { ...txtTitleCommonOpts, width: leftColWidth, ignoreOverflow: true },
  );
  isOdd = await printText(
    doc,
    printData,
    toPrintableText(printData.encounter.presentingProblemTriageNurse),
    [leftEdge(), getNextYTextPosition(doc, 4)],
    isOdd,
    { ...txtBodyCommonOpts, width: leftColWidth, trimAndEllipsize: 1200, ignoreOverflow: true },
  );

  isOdd = await printText(
    doc,
    printData,
    "Nursing Assessment:",
    [leftEdge(), getNextYTextPosition(doc, 8)],
    isOdd,
    { ...txtTitleCommonOpts, width: leftColWidth, ignoreOverflow: true },
  );
  isOdd = await printText(
    doc,
    printData,
    toPrintableText(printData.encounter.nursingAssessment),
    [leftEdge(), getNextYTextPosition(doc, 4)],
    isOdd,
    { ...txtBodyCommonOpts, width: leftColWidth, trimAndEllipsize: 1200, ignoreOverflow: true },
  );

  /**
   * Continue below both columns
   */
  const photosBottomY = photoTopY + 139;
  doc.y = Math.max(doc.y, photosBottomY) + 10;

  /**
   * Optional expects section
   */
  const expectsInformation = printData.encounter.expectsInformation || [];
  if (expectsInformation.length > 0) {
    isOdd = await boxWithText(
      doc,
      printData,
      isOdd ? "odd" : "even",
      getNextYTextPosition(doc, 2),
      "EXPECTS INFORMATION",
    );
    doc.y = getNextYTextPosition(doc, 6);

    for (let i = 0; i < expectsInformation.length; i++) {
      const [label, value] = expectsInformation[i];
      isOdd = await printRowOfColumizedText(
        doc,
        printData,
        [`${label}:`, value],
        [34, 66],
        [leftEdge(), doc.y],
        isOdd,
        {
          ...txtBodyCommonOpts,
          dividerLine: false,
          verticalPadding: 2,
          fontFamilies: ["arial-bold", "arial"],
        },
      );
    }
  }

  isOdd = await boxWithText(
    doc,
    printData,
    isOdd ? "odd" : "even",
    getNextYTextPosition(doc, 4),
    "PATIENT ENTERED HISTORY",
  );
  doc.y = getNextYTextPosition(doc, 6);

  // Provider + mobile
  isOdd = await printText(
    doc,
    printData,
    "Provider: ",
    [leftEdge(), doc.y],
    isOdd,
    { ...txtTitleCommonOpts, continued: true, ignoreOverflow: true },
  );
  isOdd = await printText(
    doc,
    printData,
    toPrintableText(printData.provider.fullName),
    [getDocCurrPosition(doc)[0], getDocCurrPosition(doc)[1]],
    isOdd,
    { ...txtBodyCommonOpts, continued: true, trimAndEllipsize: 200, ignoreOverflow: true },
  );
  isOdd = await printText(
    doc,
    printData,
    "  Mobile: ",
    [getDocCurrPosition(doc)[0], getDocCurrPosition(doc)[1]],
    isOdd,
    { ...txtTitleCommonOpts, continued: true, ignoreOverflow: true },
  );
  isOdd = await printText(
    doc,
    printData,
    toPrintableText(printData.provider.phone),
    [getDocCurrPosition(doc)[0], getDocCurrPosition(doc)[1]],
    isOdd,
    { ...txtBodyCommonOpts, trimAndEllipsize: 50, ignoreOverflow: true },
  );

  /**
  * email prints on one continuous line
  */

  isOdd = await printText(
    doc,
    printData,
    "Email: ",
    [leftEdge(), getNextYTextPosition(doc, 4)],
    isOdd,
    { ...txtTitleCommonOpts, continued: true, ignoreOverflow: true },
  );
  isOdd = await printText(
    doc,
    printData,
    toPrintableText(printData.provider.email),
    [getDocCurrPosition(doc)[0], getDocCurrPosition(doc)[1]],
    isOdd,
    { ...txtBodyCommonOpts, ignoreOverflow: true, trimAndEllipsize: 250 },
  );

  /**
   * presenting problem splits over two lines
   */

  isOdd = await printText(
    doc,
    printData,
    "Presenting Problem: ",
    [leftEdge(), getNextYTextPosition(doc)],
    isOdd,
    { ...txtTitleCommonOpts, ignoreOverflow: true },
  );

  isOdd = await printText(
    doc,
    printData,
    toPrintableText(printData.encounter.presentingProblem),
    [leftEdge(), getNextYTextPosition(doc, 4)],
    isOdd,
    { ...txtBodyCommonOpts, trimAndEllipsize: 500 },
  );

  /**
   * pain and frailty scores on one line...
   */
  await printText(
    doc,
    printData,
    "Pain Score: ",
    [leftEdge(), getNextYTextPosition(doc)],
    isOdd,
    { ...txtTitleCommonOpts, continued: true, ignoreOverflow: true },
  );
  await printText(
    doc,
    printData,
    `${printData.encounter.painScore || "-"}`,
    [getDocCurrPosition(doc)[0], getDocCurrPosition(doc)[1]],
    isOdd,
    { ...txtBodyCommonOpts, continued: true, ignoreOverflow: true },
  );
  await printText(
    doc,
    printData,
    "Frailty Score: ",
    [getDocCurrPosition(doc)[0] + 20, getDocCurrPosition(doc)[1]],
    isOdd,
    { ...txtTitleCommonOpts, continued: true, ignoreOverflow: true },
  );
  await printText(
    doc,
    printData,
    `${printData.encounter.frailtyScore || "-"}`,
    [getDocCurrPosition(doc)[0], getDocCurrPosition(doc)[1]],
    isOdd,
    { ...txtBodyCommonOpts, ignoreOverflow: true },
  );

  /**
   * Screening - Infection Risks
   */

  const screeningInfectionRisksTextArray = printData.encounter.screeningInfectionRisks as string[] || ["None"];
  const screeningInfectionRisksChunksOf4 = screeningInfectionRisksTextArray && screeningInfectionRisksTextArray.length > 0
    ? screeningInfectionRisksTextArray.reduce((acc, curr, idx) => {
      if (idx % 4 === 0) {
        acc.push([curr, "", "", ""]);
      } else {
        acc[acc.length - 1][idx % 4] = curr;
      }
      return acc;
    }
    , [] as string[][])
    : [];

  isOdd = await printText(
    doc,
    printData,
    "Screening - Infection Risks",
    [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc)],
    isOdd,
    txtTitleCommonOpts,
  );
  for (let i = 0; i < screeningInfectionRisksChunksOf4.length; i++) {
    const chunk = screeningInfectionRisksChunksOf4[i];
    isOdd = await printRowOfColumizedText(
      doc,
      printData,
      chunk,
      [25, 25, 25, 25],
      [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc, i === 0 ? 4 : 0)],
      isOdd,
      { ...txtBodyCommonOpts, dividerLine: false },
    );
  }

  /**
   * Screening - Falls Risks
   */

  const fallsRiskTextArray = printData.encounter.fallsRisk as string[][];
  const fallsRiskText3Col = fallsRiskTextArray && fallsRiskTextArray.length > 0
    ? fallsRiskTextArray.reduce((acc, curr, idx) => {
      if (idx % 3 === 0) {
        acc.push([`${curr[0]}: ${curr[1]}`, "", ""]);
      } else {
        acc[acc.length - 1][idx % 3] = `${curr[0]}: ${curr[1]}`;
      }
      return acc;
    }
    , [] as string[][])
    : null;

  if (fallsRiskText3Col) {
    isOdd = await printText(
      doc,
      printData,
      "Screening - Falls Risks",
      [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc)],
      isOdd,
      txtTitleCommonOpts,
    );
    for (let i = 0; i < fallsRiskText3Col.length; i++) {
      const chunk = fallsRiskText3Col[i];
      isOdd = await printRowOfColumizedText(
        doc,
        printData,
        chunk,
        [33, 33, 33],
        [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc, i === 0 ? 6 : 0)],
        isOdd,
        { ...txtBodyCommonOpts, dividerLine: false },
      );
    }
  }

  /**
   * Previous Medical History
   */

  const medicalHistoryTextArray = printData.encounter.pastMedicalHistory as string[];
  const medicalHistoryChunksOf2 = medicalHistoryTextArray && medicalHistoryTextArray.length > 0
    ? medicalHistoryTextArray.reduce((acc, curr, idx) => {
      if (idx % 2 === 0) {
        acc.push([curr, ""]);
      } else {
        acc[acc.length - 1][idx % 2] = curr;
      }
      return acc;
    }
    , [] as string[][])
    : null;

  if (medicalHistoryChunksOf2) {
    isOdd = await printText(
      doc,
      printData,
      "Previous Medical History",
      [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc)],
      isOdd,
      txtTitleCommonOpts,
    );
    for (let i = 0; i < medicalHistoryChunksOf2.length; i++) {
      const chunk = medicalHistoryChunksOf2[i];
      isOdd = await printRowOfColumizedText(
        doc,
        printData,
        chunk,
        [50, 50],
        [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc, i === 0 ? 6 : 0)],
        isOdd,
        { ...txtBodyCommonOpts, dividerLine: false },
      );
    }
  }

  /**
   * Previous Surgical History
   */

  const surgicalHistoryTextArray = printData.encounter.surgicalHistory as string[][];
  const surgicalHistoryText3Col = (surgicalHistoryTextArray && surgicalHistoryTextArray.length > 0)
    ? surgicalHistoryTextArray.reduce((acc, curr, idx) => {
      if (idx % 3 === 0) {
        acc.push([`${curr[0]}: ${curr[1]}`, "", ""]);
      } else {
        acc[acc.length - 1][idx % 3] = `${curr[0]}: ${curr[1]}`;
      }
      return acc;
    }
    , [] as string[][])
    : null;

  if (surgicalHistoryText3Col) {
    isOdd = await printText(
      doc,
      printData,
      "Previous Surgical History",
      [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc)],
      isOdd,
      txtTitleCommonOpts,
    );
    for (let i = 0; i < surgicalHistoryText3Col.length; i++) {
      const chunk = surgicalHistoryText3Col[i];
      isOdd = await printRowOfColumizedText(
        doc,
        printData,
        chunk,
        [33, 33, 33],
        [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc, i === 0 ? 6 : 0)],
        isOdd,
        { ...txtBodyCommonOpts, dividerLine: false },
      );
    }
  }

  /**
   * Medications
   * this one is easy, it's just a string to place under the title
   * we'll restrict it to 200 chars just in case something goes wrong in the future
   */

  isOdd = await printText(
    doc,
    printData,
    "Medications",
    [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc)],
    isOdd,
    txtTitleCommonOpts,
  );
  isOdd = await printText(
    doc,
    printData,
    printData.encounter.medications || "None",
    [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc)],
    isOdd,
    { ...txtBodyCommonOpts, trimAndEllipsize: 200 },
  );

  /**
   * Allergies
   */

  const allergiesTextArray = (printData.encounter.allergies || []) as string[][];
  // const allergiesText4Col = (allergiesTextArray && allergiesTextArray.length > 0)
  //   ? allergiesTextArray.reduce((acc, curr, idx) => {
  //     if (idx % 4 === 0) {
  //       acc.push([`${curr[0]}: (${curr[1]})`, "", "", ""]);
  //     } else {
  //       acc[acc.length - 1][idx % 4] = `${curr[0]}: (${curr[1]})`;
  //     }
  //     return acc;
  //   }
  //   , [] as string[][])
  //   : null;
  // 4-col seemed just a bit too tight
  const allergiesText3Col = (allergiesTextArray && allergiesTextArray.length > 0)
    ? allergiesTextArray.reduce((acc, curr, idx) => {
      const allergyText = curr[1] ? `${curr[0]}: (${curr[1]})` : curr[0];
      if (idx % 3 === 0) {
        acc.push([allergyText, "", ""]);
      } else {
        acc[acc.length - 1][idx % 3] = allergyText;
      }
      return acc;
    }
    , [] as string[][])
    : null;

  isOdd = await printText(
    doc,
    printData,
    "Allergies",
    [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc)],
    isOdd,
    txtTitleCommonOpts,
  );
  if (allergiesText3Col) {
    for (let i = 0; i < allergiesText3Col.length; i++) {
      const chunk = allergiesText3Col[i];
      isOdd = await printRowOfColumizedText(
        doc,
        printData,
        chunk,
        [33, 33, 33],
        [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc, i === 0 ? 6 : 0)],
        isOdd,
        { ...txtBodyCommonOpts, dividerLine: false },
      );
    }
  } else {
    isOdd = await printText(
      doc,
      printData,
      "None",
      [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc)],
      isOdd,
      { ...txtBodyCommonOpts },
    );
  }

  /**
   * Social History
   */

  const socialHistoryTextArray = printData.encounter.socialHistory as string[][];
  const socialHistoryText2Col = (socialHistoryTextArray && socialHistoryTextArray.length > 0)
    ? socialHistoryTextArray.reduce((acc, curr, idx) => {
      if (idx % 2 === 0) {
        acc.push([`${curr[0]}: ${curr[1]}`, ""]);
      } else {
        acc[acc.length - 1][idx % 2] = `${curr[0]}: ${curr[1]}`;
      }
      return acc;
    }
    , [] as string[][])
    : null;

  if (socialHistoryText2Col) {
    isOdd = await printText(
      doc,
      printData,
      "Social History",
      [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc)],
      isOdd,
      txtTitleCommonOpts,
    );
    for (let i = 0; i < socialHistoryText2Col.length; i++) {
      const chunk = socialHistoryText2Col[i];
      isOdd = await printRowOfColumizedText(
        doc,
        printData,
        chunk,
        [50, 50],
        [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc, i === 0 ? 6 : 0)],
        isOdd,
        { ...txtBodyCommonOpts, dividerLine: false },
      );
    }
  }

  return (isOdd);
};

// #endregion

// #region Generate PDF Document - Observation Chart

const addObsChartPageSub = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable,
  isOdd_: boolean,
  offsetIntoObservations: number,
  horizCapacityOfObsChart: number): Promise<number> => {

  const isOdd = isOdd_;
  printData.title2 = "Observation Record";

  // Determine which template to use based on hypercapnic respiratory failure
  const observations = printData.observations;
  const {
    hypercapnicRespiratoryFailure: hasHypercapnicRespiratoryFailure,
    o2FlowAboveZero: lastRecordedO2FlowAboveZero,
  } = getLastRecordedObservationValues(observations);

  const hypercapnicReceivingOxygen = hasHypercapnicRespiratoryFailure && lastRecordedO2FlowAboveZero;
  const printableObsChartDef = getPrintableObsChartDef(hypercapnicReceivingOxygen);
  const templateName = (() => {
    if (hasHypercapnicRespiratoryFailure) {
      if (lastRecordedO2FlowAboveZero) {
        return isOdd ? "mev-obschart-hypercapnic-flow-odd" : "mev-obschart-hypercapnic-flow-even";
      }
      return isOdd ? "mev-obschart-hypercapnic-zero-odd" : "mev-obschart-hypercapnic-zero-even";
    }
    return isOdd ? "mev-obschart-odd" : "mev-obschart-even";
  })();

  await drawPageTemplate(doc, printData, templateName);

  const nudge: [number, number] = isOdd ? [22.5, 1.5] : [0, 0]; // the chart moves a little bit on odd pages, so we put in a correction
  const [nudgeX, nudgeY] = nudge;
  let idxLastObservationProcessed = 0;
  if (observations && Array.isArray(observations) && observations.length > 0) {

    // Update O2 saturation ranges based on hypercapnic status and O2 flow
    const o2SaturationSection = printableObsChartDef.find(section => section.dataKey === obsMetricNames.o2Sats);
    if (o2SaturationSection) {
      if (!hasHypercapnicRespiratoryFailure) {
        o2SaturationSection.inputs = O2_SATURATION_LIMITS.default.ranges;
      } else if (lastRecordedO2FlowAboveZero) {
        o2SaturationSection.inputs = O2_SATURATION_LIMITS.hypercapnicWithFlow.ranges;
      } else {
        o2SaturationSection.inputs = O2_SATURATION_LIMITS.hypercapnicZeroFlow.ranges;
      }
    }

    // Update consciousness section for hypercapnic patients on oxygen
    const consciousnessSection = printableObsChartDef.find(section => section.dataKey === obsMetricNames.consciousness);
    let originalConsciousnessInputs: any[] = [];
    if (consciousnessSection && hypercapnicReceivingOxygen) {
      originalConsciousnessInputs = [
        ...consciousnessSection.inputs,
      ];
      // Modify consciousness to show as text instead of dots for hypercapnic patients on oxygen
      consciousnessSection.inputs = [
        { showOriginValue: true, range: { min: 0, max: 999 } }
      ];
    }

    const putDotOnObsChart = putDotOnChart(doc, printableObsChartDef, nudge);
    const putTwoDotsOnObsChartWithLine = putTwoDotsOnChartWithLine(doc, printableObsChartDef, nudge);

    let vitalsObsNumber = 0; // we might get observations that are not vitals
    for (let obsNumber = offsetIntoObservations; obsNumber < observations.length; obsNumber++) {

      const currObs = observations[obsNumber];
      let recordedVital = false;
      for (const chartSection of printableObsChartDef) {

        const { dataKey } = chartSection;

        const observationPresent = currObs[dataKey] !== undefined;
        if (!observationPresent) {
          continue;
        }

        if (dataKey === obsMetricNames.bloodPressure) {
          // blood pressure is the only special case
          if (currObs[dataKey].systolic && currObs[dataKey].diastolic) {
            putTwoDotsOnObsChartWithLine(dataKey, vitalsObsNumber, currObs[dataKey].systolic, currObs[dataKey].diastolic);
            recordedVital = true;
          }
        } else {
          // everything else is just a dot or the value
          const showOriginValue = chartSection.inputs.some((input: ObsInput): input is ObsInputOriginValue =>
            "showOriginValue" in input && input.showOriginValue
          );
          if (showOriginValue) {
            // show the text (not a dot)
            const value = get(currObs, dataKey);
            if (value !== undefined && value !== null) {
              const { pdfStartCoords } = chartSection;
              const drawAtCoord: [x: number, y: number] = [
                // x: (left hand edge of the cell) + little bit of padding
                pdfStartCoords[0] + ((vitalsObsNumber * cellHorizDistance) - (cellHorizDistance / 2)) + 0.2 + nudgeX,
                // y: just nudge a little bit down
                pdfStartCoords[1] + 4.5 + nudgeY,
              ];

              // Special handling for consciousness in hypercapnic patients on oxygen
              if (dataKey === obsMetricNames.consciousness && hypercapnicReceivingOxygen) {
                const yNudge = 1; // our chart isn't perfectly aligned, so we need to nudge a little bit down
                // Find the NEWS score for this consciousness value from the original definition
                const matchingInput = originalConsciousnessInputs.find((input: any) => input.value === value);
                const newsScore = matchingInput?.news;

                // Draw background rectangle with NEWS color if there's a news score
                if (newsScore) {
                  const newsColor = getNewsColorBg(newsScore);
                  const textWidth = 6.5; // Approximate width for single character
                  const textHeight = 9; // Approximate height
                  doc.rect(drawAtCoord[0] - 0.5, drawAtCoord[1] + yNudge, textWidth, textHeight).fill(newsColor);
                }

                // Print the consciousness value (single letter, uppercase)
                const textToPrint = value.toString().toUpperCase();
                // const textColor = newsScore ? "white" : "black"; // White text on colored background, black otherwise
                const textColor = "black"; // black text always
                await printText(doc, printData, textToPrint, [drawAtCoord[0], drawAtCoord[1] + yNudge], isOdd, {
                  fontSize: 7.5,
                  fontFamily: "arial",
                  ignoreOverflow: true,
                  color: textColor as any
                });
              } else {
                // Standard text handling for other metrics
                const textToPrint = (() => {
                  let printTextTmp = "";
                  if (dataKey === obsMetricNames.riskScore) {
                    // the risk score (news2 value) is an object not just some text
                    if (currObs[dataKey]?.score !== undefined) {
                      // Check if the NEWS2 score is incomplete - if so, show a dash instead
                      if (currObs[dataKey]?.incomplete === true) {
                        printTextTmp = "-";
                      } else {
                        const riskScoreScore = currObs[dataKey].score;
                        // const riskScoreRisk = currObs[dataKey].risk;
                        printTextTmp = `${riskScoreScore}`;
                      }
                    }
                  } else {
                    printTextTmp = currObs[dataKey].toString();
                  }
                  // limit to 4 characters
                  return (printTextTmp.length > 4 ? printTextTmp.substring(0, 4) : printTextTmp);
                })();
                await printText(doc, printData, textToPrint, drawAtCoord, isOdd, { fontSize: 7.5, fontFamily: "arial", ignoreOverflow: true });
              }
            }
          } else {
            // just the Unresp. row on consciousness chart needs a nudge, everything else is fine
            const additionalNudge = (dataKey === obsMetricNames.consciousness && currObs && currObs[dataKey] === "U") ? ([0, -1.5] as [number, number]) : undefined;
            putDotOnObsChart(dataKey, vitalsObsNumber, currObs[dataKey], additionalNudge);
          }
          recordedVital = true;
        }
      } // end-for-each chart section

      if (recordedVital) {
        // fill in the date, time, and nurse-initial
        const obsDateTime = currObs.created;
        const obsDateDDMMM = obsDateTime ? moment(obsDateTime).format("DD/MM") : "";
        const obsTime = obsDateTime ? obsDateTime.substring(11, 16) : "";
        const nurseInitals = getInitialsObsChart((currObs.actingAsName ?? currObs.createdByName) || "");
        const drawAtCoord: [x: number, y: number] = [
          tlhsCellHeader[0] + ((vitalsObsNumber * cellHorizDistance) - (cellHorizDistance / 2)) + 0.2 + nudgeX,
          tlhsCellHeader[1] + 1.5 + nudgeY,
        ];
        await printText(doc, printData, obsDateDDMMM, drawAtCoord, isOdd, { fontSize: 7.5, fontFamily: "arial", ignoreOverflow: true });
        await printText(doc, printData, obsTime, [drawAtCoord[0], drawAtCoord[1] + cellVertDistance], isOdd, { fontSize: 7.5, fontFamily: "arial", ignoreOverflow: true });
        await printText(doc, printData, nurseInitals, [drawAtCoord[0], drawAtCoord[1] + cellVertDistance * 2], isOdd, { fontSize: 7.5, fontFamily: "arial", ignoreOverflow: true });

        vitalsObsNumber++;
      }
      idxLastObservationProcessed = obsNumber;
      if (vitalsObsNumber >= horizCapacityOfObsChart) {
        break;
      }
    } // end-for-each observation
  }

  const printTestDots = false;
  if (printTestDots) {
    // fill the grid with dots (i.e. sanity check grid is uniform)
    for (let i = 0; i < countCellsAcross; i++) {
      for (let j = 0; j < countCellsHeigh; j++) {
        drawSolidCircle(
          doc,
          [
            tlhsCell[0] + nudge[0] + i * cellHorizDistance,
            tlhsCell[1] + nudge[1] + j * cellVertDistance,
          ],
          dotRadius,
          "green",
        );
      }
    }
  }

  return (idxLastObservationProcessed);
};

const addObsChartPage = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable,
  isOdd_: boolean,
  horizCapacityOfObsChart: number): Promise<boolean> => {

  const observations = printData.observations;
  let idxLastObservationProcessed = 0;
  let isOdd = isOdd_;

  if (observations && Array.isArray(observations) && observations.length > 0) {
    while (idxLastObservationProcessed < observations.length) {
      // eslint-disable-next-line no-await-in-loop
      idxLastObservationProcessed = await addObsChartPageSub(
        doc,
        printData,
        isOdd,
        idxLastObservationProcessed,
        horizCapacityOfObsChart,
      );
      if (idxLastObservationProcessed >= (observations.length - 1)) {
        break;
      }
      doc.addPage();
      isOdd = !isOdd;
      // for the next iteration, we need to start at the next observation
      idxLastObservationProcessed++;
    }
  } else {
    // no observations, so just draw the page template
    await drawPageTemplate(doc, printData, isOdd ? "mev-obschart-odd" : "mev-obschart-even");
  }

  return (isOdd);
};

// #endregion

// #region Generate PDF Document - Neurological Observations

const limbNeuroValueToAbbreviation = (value: string | null): string | null => {
  if (value) {
    switch (value) {
      case "normal":
        return "N";
      case "mottled":
        return "M";
      case "dusky":
        return "D";
      case "pale":
        return "P";
      case "flushed":
        return "F";
      case "hot":
        return "H";
      case "warm":
        return "W";
      case "cool":
        return "Cl";
      case "cold":
        return "Cd";
      case "impaired":
        return "I";
      case "absent":
        return "A";
      case "Doppler only": // there is code that overwrites this value from the server - changing here rather than there (easier for now - should refactor)
      case "doppler":
        return "D";
      case "weak":
        return "Wk";
      default:
        return value;
    }
  }

  return null;
};

const addNeuroObsChartPageSub = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable,
  isOdd_: boolean,
  offsetIntoObservations: number,
  horizCapacityOfObsChart: number): Promise<number> => {

  const isOdd = isOdd_;
  // printData.title2 = "Neuro Record"; // not used anymore
  await drawPageTemplate(doc, printData, isOdd ? "mev-template-neuro-odd" : "mev-template-neuro-even");

  const observations = printData.neuroObservations;

  const nudge: [number, number] = isOdd ? [22.5, 1.5] : [0, 0]; // the chart moves a little bit on odd pages, so we put in a correction
  const [nudgeX, nudgeY] = nudge;
  let idxLastObservationProcessed = 0;
  if (observations && Array.isArray(observations) && observations.length > 0) {

    const limbNeuroDateTimesPrinted: string[] = [];
    const putDotOnObsChart = putDotOnChart(doc, printableNeuroObsChartDef, nudge, [cellHorizDistanceNeuro, cellVertDistanceNeuro]);

    let visibleObsCount = 0; // we might get observations that are not printed to the chart
    let currentNeuroLimbRow = -1;
    const maxNeuroLimbRows = 7; // 0th counting
    //console.log('obs', observations, printableNeuroObsChartDef);
    for (let obsNumber = offsetIntoObservations; obsNumber < observations.length; obsNumber++) {

      const currObs = observations[obsNumber];
      if (currObs.neuro?.limbNeuro) currentNeuroLimbRow++;
      let recordedObs = false;
      let limbMovementLR = [null, null] as [string | null, string | null];
      let chartSectionIdx = -1;
      for (const chartSection of printableNeuroObsChartDef) {

        chartSectionIdx++;
        const { dataKey } = chartSection;

        const currObsValue = get(currObs, dataKey);
        const observationPresent = currObsValue !== undefined;

        // limb movement is a special case
        if (dataKey === obsMetricNames.limbMovRightArm || dataKey === obsMetricNames.limbMovRightLeg) {
          // store right, reset left, wait for the left value on the next iteration
          limbMovementLR = [currObsValue || null, null];
          continue;
        } else if (dataKey === obsMetricNames.limbMovLeftArm || dataKey === obsMetricNames.limbMovLeftLeg) {
          // store left, print the pair
          limbMovementLR[1] = currObsValue || null;
          if (limbMovementLR[0] || limbMovementLR[1]) {
            const areMovementsEqual = limbMovementLR[0] === limbMovementLR[1];
            if (areMovementsEqual) {
              // just put a dot on the value (in column 1)
              putDotOnObsChart(dataKey, visibleObsCount, limbMovementLR[0] as any, [-12.95, 0]);
            } else {
              // need to put an 'L' for left and 'R' for right
              const previousChartSection = printableNeuroObsChartDef[chartSectionIdx - 1];

              const currChartSection = [previousChartSection, chartSection];
              const charToPrint = ["R", "L"];
              // Use the stored values from limbMovementLR instead of re-fetching
              // These values were already retrieved and stored when processing the right/left sections
              const valuesToUse = [limbMovementLR[0], limbMovementLR[1]];
              for (let i = 0; i < 2; i++) {
                const value = valuesToUse[i];
                if (value !== null && value !== undefined) {
                  const writeAtCoords = findDrawAtCoords(
                    currChartSection[i],
                    visibleObsCount,
                    [-3 + nudgeX, -3 + nudgeY], // nudge - same for both
                    value,
                    [cellHorizDistanceNeuro, cellVertDistanceNeuro],
                  );
                  await printText(doc, printData, charToPrint[i], writeAtCoords, isOdd, { fontSize: 7.5, fontFamily: "arial" });
                }
              }
            }
            recordedObs = true;
          }
          continue;
        }

        const isLimbNeuro = dataKey.includes("neuro.limbNeuro.");
        if (isLimbNeuro) {

          // // test grid
          // const countCellsAcrossLn = 7;
          // const countCellsHeighLn = 7;
          // const heightLimbNeuroRow = 18;
          // const neuroLimbHorizDist = 46.9;
          // const neuroLeftArmStartX = 84;
          // const neuroLeftArmStartY = 690;
          // const neuroRightArmStartX = 115;
          // const neuroRightArmStartY = neuroLeftArmStartY;
          // const neuroLegNudgeY = 9;

          // for (let i = 0; i < countCellsAcrossLn; i++) {
          //   for (let j = 0; j < countCellsHeighLn; j++) {
          //     drawSolidCircle(
          //       doc,
          //       [
          //         neuroLeftArmStartX + nudgeX + (i * neuroLimbHorizDist),
          //         neuroLeftArmStartY + nudgeY + (j * heightLimbNeuroRow),
          //       ],
          //       dotRadius,
          //       "green",
          //     );
          //     drawSolidCircle(
          //       doc,
          //       [
          //         neuroRightArmStartX + nudgeX + (i * neuroLimbHorizDist),
          //         neuroRightArmStartY + nudgeY + (j * heightLimbNeuroRow),
          //       ],
          //       dotRadius,
          //       "pink",
          //     );
          //     drawSolidCircle(
          //       doc,
          //       [
          //         neuroLeftArmStartX + nudgeX + (i * neuroLimbHorizDist),
          //         neuroLeftArmStartY + nudgeY + (j * heightLimbNeuroRow) + neuroLegNudgeY,
          //       ],
          //       dotRadius,
          //       "yellow",
          //     );
          //   }
          // }
          if (currentNeuroLimbRow >= maxNeuroLimbRows) continue;
          const currObsValueLn = limbNeuroValueToAbbreviation(currObsValue);
          const { pdfStartCoords } = chartSection;
          const drawAtCoord: [x: number, y: number] = [
            // x: no need to change
            pdfStartCoords[0] + nudgeX,
            // y: just nudge a little bit down
            pdfStartCoords[1] + (currentNeuroLimbRow * heightLimbNeuroRow) + nudgeY,
          ];
          await printText(doc, printData, currObsValueLn || "", drawAtCoord, isOdd, { fontSize: 7.5, fontFamily: "arial" });

          // print date time for limb neuro
          const obsDateTime = currObs.created;
          const obsDateDDMMM = obsDateTime ? moment(obsDateTime).format("DD/MM") : "";
          const obsTime = obsDateTime ? obsDateTime.substring(11, 16) : "";
          if (!limbNeuroDateTimesPrinted.includes(obsDateTime) && currObsValueLn) {
            const drawAtCoordTime: [x: number, y: number] = [
              neuroLeftArmStartX - 33 + nudgeX,
              neuroLeftArmStartY + (currentNeuroLimbRow * heightLimbNeuroRow) + nudgeY,
            ];
            console.log("time at", obsTime, drawAtCoordTime);
            await printText(doc, printData, obsDateDDMMM, drawAtCoordTime, isOdd, { fontSize: 7.5, fontFamily: "arial", ignoreOverflow: true });
            await printText(doc, printData, obsTime, [drawAtCoordTime[0], drawAtCoordTime[1] + 8], isOdd, { fontSize: 7.5, fontFamily: "arial", ignoreOverflow: true });
            limbNeuroDateTimesPrinted.push(obsDateTime);
          }
          continue;
        }

        if (!observationPresent) {
          continue;
        }

        const showOriginValue = chartSection.inputs.some((input: ObsInput): input is ObsInputOriginValue =>
          "showOriginValue" in input && input.showOriginValue
        );
        if (showOriginValue) {
          // show the text (not a dot)
          const value = get(currObs, dataKey);
          if (value !== undefined && value !== null) {
            const { pdfStartCoords } = chartSection;
            const drawAtCoord: [x: number, y: number] = [
              pdfStartCoords[0] + ((visibleObsCount * cellHorizDistanceNeuro) - (cellHorizDistanceNeuro / 2)) + 0.2 + nudgeX,
              pdfStartCoords[1] + 4.5 + nudgeY,
            ];
            await printText(doc, printData, value.toString(), drawAtCoord, isOdd, { fontSize: 7.5, fontFamily: "arial", ignoreOverflow: true });
          }
        } else {
          // print a dot
          putDotOnObsChart(dataKey, visibleObsCount, currObsValue);
        }

        recordedObs = true;
      } // end-for-each chart section
      if (recordedObs) {
        // fill in the date, time, and nurse-initial
        const obsDateTime = currObs.created;
        const obsDateDDMMM = obsDateTime ? moment(obsDateTime).format("DD/MM") : "";
        const obsTime = obsDateTime ? obsDateTime.substring(11, 16) : "";
        const nurseInitals = getInitialsObsChart((currObs.actingAsName ?? currObs.createdByName) || "");
        const drawAtCoord: [x: number, y: number] = [
          initalsNtlhsCell[0] + ((visibleObsCount * cellHorizDistanceNeuro) - (cellHorizDistanceNeuro / 2)) + 0.2 + nudgeX,
          initalsNtlhsCell[1] + 1.5 + nudgeY,
        ];
        await printText(doc, printData, obsDateDDMMM, drawAtCoord, isOdd, { fontSize: 7.5, fontFamily: "arial", ignoreOverflow: true });
        await printText(doc, printData, obsTime, [drawAtCoord[0], drawAtCoord[1] + cellVertDistanceNeuro], isOdd, { fontSize: 7.5, fontFamily: "arial", ignoreOverflow: true });
        await printText(doc, printData, nurseInitals, [drawAtCoord[0], drawAtCoord[1] + cellVertDistanceNeuro * 2], isOdd, { fontSize: 7.5, fontFamily: "arial", ignoreOverflow: true });

        visibleObsCount++;
      }

      idxLastObservationProcessed = obsNumber;
      if (visibleObsCount >= horizCapacityOfObsChart) {
        break;
      }
    } // end-for-each observation
  }

  // might do some test printing to check alignment here...

  return (idxLastObservationProcessed);
};

const addNeuroObsChartPage = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable,
  isOdd_: boolean,
) => {

  const observations = (printData.neuroObservations || []);
  let idxLastObservationProcessed = 0;
  let isOdd = isOdd_;
  const hasNeuroObs = observations && Array.isArray(observations) && observations.length > 0;
  const horizCapacityOfObsChart = 7;

  if (hasNeuroObs) {
    doc.addPage();
    while (idxLastObservationProcessed < observations.length) {
      // eslint-disable-next-line no-await-in-loop
      idxLastObservationProcessed = await addNeuroObsChartPageSub(
        doc,
        printData,
        isOdd,
        idxLastObservationProcessed,
        horizCapacityOfObsChart,
      );
      if (idxLastObservationProcessed >= (observations.length - 1)) {
        break;
      }
      doc.addPage();
      isOdd = !isOdd;
      idxLastObservationProcessed++;
    }
    return isOdd;
  } else {
    // if there aren't any neurological observations, don't include the page, just skip it
    // Return the original oddness state since no pages were added
    return !isOdd_;
  }
};


// #endregion

// #region Generate PDF Document - Investigations etc.


/**
 * Adds the investigations page to the PDF document.
 *
 * TODO: I'm assuming this page never overflows (because we only ever do a max of two investigations...)
 *
 * @param doc - The PDF document to add the page to.
 * @param printData - The printable data for the encounter.
 * @param isOdd_ - A boolean indicating if the page is odd or even.
 * @returns A Promise that resolves when the investigations page is added.
 */
const addInvestigationsEtcPage = async (
  doc: typeof PDFDocument,
  printData: EncounterPrintable,
  isOdd_: boolean) => {

  let isOdd = isOdd_;
  await drawPageTemplate(doc, printData, isOdd ? "mev-investigations-odd" : "mev-investigations-even");
  const txtBodyCommonOpts = {
    fontSize: 10,
    fontFamily: "arial",
    width: 495,
  } as const;

  /**
   * Investigations (Test Results)
   */

  doc.y = startOfPageY + 20;
  if (printData.testResults && Array.isArray(printData.testResults) && printData.testResults.length > 0) {

    const investigationsTableData = printData.testResults.map((obs) => {
      const urinalysisResult = obs.urinalysis;
      // if (urinalysisResult && "pregnancy" in urinalysisResult) {
      //   return ([
      //     obs.created ? moment(obs.created).format("HH:mm[\n]DD/MM/YYYY") : "",
      //     getInitialsObsChart(obs.createdByName || ""),
      //     "PREGNANCY TEST\nResult",
      //     `\n${urinalysisResult.pregnancy}`,
      //   ]);
      // }
      if (urinalysisResult) {

        const urinalysisFactors = Object.keys(urinalysisResult)
          .map((it) => {
            const tmpVal = camelCaseToTitleCase(it);
            if (tmpVal === "Distream") {
              return ("Midstream Urine Sent");
            }

            return (tmpVal);
          })
          .join("\n");
        const urinalysisValues = `\n${Object.values(urinalysisResult).join("\n")}`;

        return ([
          obs.created ? moment(obs.created).format("HH:mm[\n]DD/MM/YYYY") : "",
          // getInitialsObsChart(obs.createdByName || ""),
          cleanCreatedNameObsChart((obs.actingAsName ?? obs.createdByName) || ""),
          `URINALYSIS\n${urinalysisFactors}`,
          urinalysisValues,
        ]);
      }

      return (null);
    }).filter((row) => row !== null) as string[][];

    for (let i = 0; i < investigationsTableData.length; i++) {
      const investigationRow = investigationsTableData[i];
      isOdd = await printRowOfColumizedText(
        doc,
        printData,
        investigationRow,
        [16.5, 13, (100 - 16.5 - 13) / 2, (100 - 16.5 - 13) / 2],
        [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc, 0)],
        isOdd,
        { ...txtBodyCommonOpts, dividerLine: true },
      );
    }
  } else {
    isOdd = await printText(
      doc,
      printData,
      "No Investigations",
      [getEdgeSpacing(isOdd).leftHandEdgeTxt, startOfPageY + 20],
      isOdd,
      txtBodyCommonOpts,
    );
  }

  /**
   * Ventilator Observations
   */

  const ventilatorRows: string[][] = [];
  if (printData.ventilatorObservations && Array.isArray(printData.ventilatorObservations) && printData.ventilatorObservations.length > 0) {
    for (const obs of printData.ventilatorObservations) {
      const v = obs.ventilator || {};
      // Build label and value columns from ventilator keys with values
      const keys = Object.keys(v).filter((k) => v[k] !== undefined && v[k] !== null && v[k] !== "");
      if (keys.length === 0) continue;

      const labels = keys
        .map((k) => {
          const metricKey = (`ventilator${k.charAt(0).toUpperCase()}${k.slice(1)}`) as any; // e.g. ventilatorTidalVolume
          return obsMetricToLabel(metricKey);
        })
        .join("\n");
      const values = `\n${keys.map((k) => String(v[k])).join("\n")}`;

      ventilatorRows.push([
        obs.created ? moment(obs.created).format("HH:mm[\n]DD/MM/YYYY") : "",
        cleanCreatedNameObsChart((obs.actingAsName ?? obs.createdByName) || ""),
        `VENTILATOR\n${labels}`,
        values,
      ]);
    }
  }

  if (ventilatorRows.length > 0) {
    // Section header like others, with overflow handling
    isOdd = await boxWithText(doc, printData, isOdd ? "odd" : "even", getNextYTextPosition(doc), "VENTILATOR OBSERVATIONS");

    for (let i = 0; i < ventilatorRows.length; i++) {
      const row = ventilatorRows[i];
      isOdd = await printRowOfColumizedText(
        doc,
        printData,
        row,
        [16.5, 13, (100 - 16.5 - 13) / 2, (100 - 16.5 - 13) / 2],
        [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc, i === 0 ? 6 : 0)],
        isOdd,
        { ...txtBodyCommonOpts, dividerLine: true },
      );
    }
  }

  /**
   * Observations Notes / Activities
   */

  const obsNotesNotComms = (printData.notes || []).filter((note) => (note.type || "") !== "communication");
  const obsNotesFromChart = (printData.observations || []).filter((obs: Observation) => obs.note);
  const obsNotes = [
    ...obsNotesNotComms,
    ...obsNotesFromChart,
  ].sort((a, b) => {
    const aDate = a.created ? moment(a.created).valueOf() : 0;
    const bDate = b.created ? moment(b.created).valueOf() : 0;
    return (aDate - bDate);
  });

  isOdd = await boxWithText(doc, printData, isOdd ? "odd" : "even", getNextYTextPosition(doc), "OBSERVATION / WAITING ROOM NOTES");

  if (obsNotes.length > 0) {
    const obsNotesTableData: Array<[string, string, string]> = obsNotes.map((note) => {
      const rawObsNote = (note.text || note.note || "").trim();
      // we now need to trim all leading and trailing whitespace from each line
      const obsNoteCleaned = rawObsNote
        .split("\n")
        .map((line1: string) => (line1 || "").trim())
        .join("\n");

      return ([
        note.created ? moment(note.created).format("HH:mm[\n]DD/MM/YYYY") : "",
        // getInitialsObsChart(note.createdByName || ""),
        cleanCreatedNameObsChart((note.actingAsName ?? note.createdByName) || ""),
        obsNoteCleaned,
      ]);
    });

    doc.y += 6;
    for (let i = 0; i < obsNotesTableData.length; i++) {
      const obsNoteRow = obsNotesTableData[i];
      isOdd = await printRowOfColumizedText(
        doc,
        printData,
        obsNoteRow,
        [16.5, 13, 100 - 16.5 - 13],
        [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc, i === 0 ? 4 : 0)],
        isOdd,
        { ...txtBodyCommonOpts, dividerLine: true },
      );
    }

  } else {
    isOdd = await printText(
      doc,
      printData,
      "No Activity",
      [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc)],
      isOdd,
      txtBodyCommonOpts,
    );
  }

  /**
   * Communications
   */

  const commNotes = (printData.notes || []).filter((note) => (note.type || "") === "communication");

  isOdd = await boxWithText(doc, printData, isOdd ? "odd" : "even", getNextYTextPosition(doc), "MESSAGES SENT TO PATIENT");

  if (commNotes.length > 0) {

    const commNotesTableData: Array<[string, string, string]> = commNotes.map((note) => {
      return ([
        note.created ? moment(note.created).format("HH:mm[\n]DD/MM/YYYY") : "",
        // getInitialsObsChart(note.createdByName || ""),
        cleanCreatedNameObsChart((note.actingAsName ?? note.createdByName) || ""),
        (note.text || "").trim(),
      ]);
    });

    doc.y += 6;
    for (let i = 0; i < commNotesTableData.length; i++) {
      const commNoteRow = commNotesTableData[i];
      isOdd = await printRowOfColumizedText(
        doc,
        printData,
        commNoteRow,
        [16.5, 13, 100 - 16.5 - 13],
        [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc, i === 0 ? 4 : 0)],
        isOdd,
        { ...txtBodyCommonOpts, dividerLine: true },
      );
    }

  } else {
    isOdd = await printText(
      doc,
      printData,
      "No Communications",
      [getEdgeSpacing(isOdd).leftHandEdgeTxt, getNextYTextPosition(doc)],
      isOdd,
      txtBodyCommonOpts,
    );
  }

  return (isOdd);
};
// #endregion

const printTestBarcode = async (doc: typeof PDFDocument) => {
  await addSVG(
    doc,
    "test_barcode.svg",
    [10, 25],
    undefined,
    {
      width: 45,
      height: 200,
      preserveAspectRatio: "xMinYMin meet"
    },
  );
};

const registerPdfFonts = async (doc: typeof PDFDocument) => {
  const font1 = await fetch("/arial.ttf");
  doc.registerFont("arial", await font1.arrayBuffer());
  const font2 = await fetch("/arial_bold.ttf");
  doc.registerFont("arial-bold", await font2.arrayBuffer());
  const font3 = await fetch("/arial_italic.ttf");
  doc.registerFont("arial-italic", await font3.arrayBuffer());
  const font4 = await fetch("/arial_bold_italic.ttf");
  doc.registerFont("arial-bold-italic", await font4.arrayBuffer());
};

const addBufferedPageNumbers = async (doc: typeof PDFDocument, printData: EncounterPrintable) => {
  const range = doc.bufferedPageRange();
  const totalPages = range.count;

  for (let i = range.start; i < range.start + range.count; i++) {
    doc.switchToPage(i);
    const pageNumber = i + 1;
    const isPageOdd = (pageNumber % 2 === 1);
    await addPageNumber(doc, printData, pageNumber, totalPages, isPageOdd);
  }
};

const createPdfFileName = (printData: EncounterPrintable) => {
  const patientName = `${printData.patient.surname}-${printData.patient.forename}`;
  const dateNow = moment().format("YYYYMMDD-HH:mm");
  return `trier-${patientName}-${dateNow}.pdf`.toLocaleLowerCase();
};

const openPdfBlobInNewTab = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const openedWindow = window.open(url, "_blank");

  if (!openedWindow) {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.target = "_blank";
    link.click();
  }

  // Delay cleanup to ensure the browser has started loading the URL.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const generatePDF = async (
  encounter: EncounterPrintable,
  observationsOnly: boolean,
  settings?: any,
) => {

  const sourceEncounter = USE_MOCK_ENCOUNTER_FOR_PDF_PREVIEW
    ? (mockEncounter01 as unknown as EncounterPrintable)
    : encounter;
  const printData = encounter2PrintableEncounter(sourceEncounter, settings);

  const doc = new PDFDocument({
    size: "A4",
    // margins: { top: 0, bottom: 50, left: 0, right: 0 },
    margin: 0,
    bufferPages: true, // Enable page buffering to allow switching between pages (i.e. we need this to retrospectively add page numbers)
  });
  const stream = doc.pipe(BlobStream());
  doc.info.Title = "Trier Observations Chart";
  doc.info.Author = "Trier Solutions";
  doc.info.CreationDate = new Date();

  // doc.registerFont("arial", path.join(__dirname, "fonts", "arial.ttf"));
  // doc.registerFont("arial-bold", path.join(__dirname, "fonts", "arial_bold.ttf"));
  // doc.registerFont("arial-italic", path.join(__dirname, "fonts", "arial_italic.ttf"));
  // doc.registerFont("arial-bold-italic", path.join(__dirname, "fonts", "arial_bold_italic.ttf"));

  await registerPdfFonts(doc);

  printData.patient.surname = printData.patient.surname.toUpperCase();

  let isCurrPageOdd = true;

  if (observationsOnly) {

    // -- page.1 (observations chart)
    // [START]: TEMPORARY TEST (they've requested to have the test barcode here)
    await printTestBarcode(doc);
    // [END]: TEMPORARY TEST
    isCurrPageOdd = await addObsChartPage(doc, printData, isCurrPageOdd, countCellsAcross);

    // -- page.2- (neurological observations)
    await addNeuroObsChartPage(doc, printData, !isCurrPageOdd);

  } else {

    // -- page.1 (visit record)
    // [START]: TEMPORARY TEST (they've requested to have the test barcode here)
    await printTestBarcode(doc);
    // [END]: TEMPORARY TEST
    isCurrPageOdd = await addPage1(doc, printData);

    // -- page.2 (observations chart)
    doc.addPage();
    isCurrPageOdd = await addObsChartPage(doc, printData, !isCurrPageOdd, countCellsAcross);

    // -- page.3 (neurological observations)
    // doc.addPage(); // page is added within the next call (if it's needed)
    isCurrPageOdd = await addNeuroObsChartPage(doc, printData, !isCurrPageOdd);

    // -- page.4-and-onwards (investigations, observation notes / activities, messages sent to patient)
    doc.addPage();
    isCurrPageOdd = await addInvestigationsEtcPage(doc, printData, !isCurrPageOdd);
  }

  // -- add page numbers
  await addBufferedPageNumbers(doc, printData);

  // -- finalize

  doc.end();

  stream.on("finish", () => {
    console.log("PDF generation complete");

    const fileName = createPdfFileName(printData);

    const blob = stream.toBlob("application/pdf");
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      // Safari really doesn't like us using window.open, so we just click the link, and let the browser handle this filetype
      link.click();
    } else {
      window.open(url, "_blank");
    }
    // clean up
    URL.revokeObjectURL(url);
  });
};

export const generateMevSummarySectionPdf = async (
  encounter: EncounterPrintable,
  settings?: any,
) => {
  const sourceEncounter = USE_MOCK_ENCOUNTER_FOR_PDF_PREVIEW
    ? (mockEncounter01 as unknown as EncounterPrintable)
    : encounter;
  const printData = encounter2PrintableEncounter(sourceEncounter, settings);
  const doc = new PDFDocument({
    size: "A4",
    margin: 0,
    bufferPages: true,
  });
  const stream = doc.pipe(BlobStream());

  doc.info.Title = "Trier Observations Chart - MEV Summary";
  doc.info.Author = "Trier Solutions";
  doc.info.CreationDate = new Date();

  await registerPdfFonts(doc);

  printData.patient.surname = printData.patient.surname.toUpperCase();

  // Keep same first-section content as the full report.
  await printTestBarcode(doc);
  await addPage1(doc, printData);
  await addBufferedPageNumbers(doc, printData);

  doc.end();

  await new Promise<void>((resolve) => {
    stream.on("finish", () => {
      console.log("MEV summary PDF generation complete");
      const fileName = createPdfFileName(printData);
      const blob = stream.toBlob("application/pdf");
      openPdfBlobInNewTab(blob, fileName);
      resolve();
    });
  });
};
